'use client';

import React, { useEffect, useState } from 'react';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import { cj } from 'src/utils/classNames';
import { WithChildren, WithClassName } from 'src/utils/UtilityTypes';

interface Props extends WithChildren, WithClassName {
	/**
	 * Should be passed to the scrollable element that needs shadows.
	 */
	scrollableRef: React.RefObject<HTMLElement | null>;
	/**
	 * Class to apply to the gradient shadows, defaults to `from-main-bg`.
	 */
	gradientFromClassName?: string;
	/**
	 * Defaults to `horizontal`.
	 */
	orientation?: 'horizontal' | 'vertical';
	/**
	 * Defaults to `mobile`.
	 */
	screenSize?: 'mobile' | 'all';
	/**
	 * Can be used to explicitly disable the shadows.
	 */
	isDisabled?: boolean;
}

/**
 * Adds gradient shadows indicating scrollable content.
 */
const ScrollShadows = ({
	children,
	className,
	scrollableRef,
	gradientFromClassName = 'from-main-bg',
	orientation = 'horizontal',
	screenSize = 'mobile',
	isDisabled = false,
}: Props) => {
	const isMobileScreenSize = useIsMobileScreenSize();
	const [hasStartContent, setHasStartContent] = useState(false);
	const [hasEndContent, setHasEndContent] = useState(false);

	const isEnabledForScreenSize =
		screenSize === 'all' || (screenSize === 'mobile' && isMobileScreenSize);

	const isEnabled = isEnabledForScreenSize && !isDisabled;

	useEffect(() => {
		if (!scrollableRef.current || !isEnabled) {
			return;
		}

		const updateHasContent = (target: HTMLElement) => {
			const { newHasStartContent, newHasEndContent } = (() => {
				if (orientation === 'horizontal') {
					return {
						newHasStartContent: target.scrollLeft > 0,
						newHasEndContent:
							target.scrollLeft < target.scrollWidth - target.clientWidth,
					};
				}

				return {
					newHasStartContent: target.scrollTop > 0,
					newHasEndContent:
						target.scrollTop < target.scrollHeight - target.clientHeight,
				};
			})();

			setHasStartContent(newHasStartContent);
			setHasEndContent(newHasEndContent);
		};

		const handleScroll = (event: Event) => {
			const target = event.target as HTMLElement;

			updateHasContent(target);
		};

		const target = scrollableRef.current;
		updateHasContent(target);

		target.addEventListener('scroll', handleScroll);

		return () => {
			target.removeEventListener('scroll', handleScroll);
		};
	}, [isEnabled, orientation]);

	if (!isEnabled) {
		return children;
	}

	const baseClassName = cj(
		'absolute z-30 to-transparent',
		orientation === 'horizontal' ? 'h-full w-6' : 'h-10 w-full',
		gradientFromClassName
	);

	return (
		<div className={cj('relative', className)}>
			{hasStartContent && (
				<div
					className={cj(
						baseClassName,
						orientation === 'horizontal' ? 'bg-linear-to-r' : 'bg-linear-to-b',
						'top-0 left-0'
					)}
				/>
			)}

			{children}

			{hasEndContent && (
				<div
					className={cj(
						baseClassName,
						orientation === 'horizontal' ? 'bg-linear-to-l' : 'bg-linear-to-t',
						'right-0 bottom-0'
					)}
				/>
			)}
		</div>
	);
};

export default React.memo(ScrollShadows);
